import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';

import {
	CreateFlexPeriodAction,
	DeleteFlexPeriodAction,
	GetFlexPeriodsAction,
	UpdateFlexPeriodAction,
} from 'app/ngrx/flex-periods/flex-periods.actions';
import { MilitaryTimeStringPipe } from 'app/pipes/military-time-string.pipe';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { CreateFlexPeriodComponent } from '../admin/flex-period/create-flex-period/create-flex-period.component';
import { AppState } from '../ngrx/app-state/app-state';
import { getFlexPeriodById, getFlexPeriodCollection } from '../ngrx/flex-periods/flex-periods.selectors';
import { getIsLoadingFlexPeriods } from '../ngrx/flex-periods/flex-periods.state';
import { HttpService } from './http-service';

export enum FlexVisibility {
	Visible = 'visible',
	Limited = 'limited',
	Restricted = 'restricted',
}

export interface FlexPeriod {
	id: number;
	name: string;
	schedules: FlexSchedule[];
	signup_window_start: string;
	signup_window_end: string;
	visibility: FlexVisibility;
	visibility_student_ids: number[];
	visibility_group_ids: number[];
	deleted_at?: string;
	created_at?: string;
}

export interface SignUpWindowSessions {
	earliest: string;
	latest: string;
}

export interface FlexSchedule {
	id?: number;
	days_of_week: number[];
	start_hour: number;
	start_minute: number;
	end_hour: number;
	end_minute: number;
}

export interface AddFlexScheduleRequest extends FlexSchedule {
	flex_period?: number;
	name?: string;
	schedules: FlexSchedule[];
}

export interface UpdateFlexPeriodRequest {
	flex_period?: Partial<FlexPeriod>;
	update_visibility_students_groups: boolean;
}

export interface GetFlexPeriodStatsRequest {
	schedule_id?: number; // this is a flex schedule id
	start_time: Date;
	end_time?: Date;
}

export interface GetFlexPeriodStatsResponse {
	id?: number;
	activity_count: number;
	signup_slots?: number;
	signup_count?: number;
	available_students?: number;
}

export enum DaysOfWeek {
	Sun,
	Mon,
	Tue,
	Wed,
	Thu,
	Fri,
	Sat,
}

export enum DaysOfWeekShort {
	S,
	M,
	T,
	W,
	Th,
	F,
	Sat,
}

export type DateStatus = 'past' | 'today' | 'future';

@Injectable({
	providedIn: 'root',
})
export class FlexPeriodService {
	constructor(private http: HttpService, private store: Store<AppState>, public matDialog: MatDialog) {}

	//#region ngrx

	flexPeriods$ = this.store.select(getFlexPeriodCollection);
	flexPeriod$ = (id: number) => this.store.select(getFlexPeriodById(id));
	isLoadingFlexPeriods$ = this.store.select<boolean>(getIsLoadingFlexPeriods);
	isLoadedFlexPeriods$ = this.store.select<boolean>(getIsLoadingFlexPeriods);

	RefreshFlexPeriodsIfEmpty() {
		this.flexPeriods$.pipe(take(1)).subscribe((flexPeriods) => {
			if (flexPeriods.length === 0) {
				this.RefreshFlexPeriods();
			}
		});
	}

	RefreshFlexPeriods() {
		this.store.dispatch(GetFlexPeriodsAction());
	}

	CreateFlexPeriod(flexPeriod: FlexPeriod) {
		this.store.dispatch(CreateFlexPeriodAction({ flexPeriod }));
	}

	UpdateFlexPeriod(id: number, partial: Partial<FlexPeriod>, updateStudentsGroupsVisibility = false) {
		this.store
			.select(getFlexPeriodById(id))
			.pipe(take(1))
			.subscribe((original) => {
				const update = { id, changes: partial };
				this.store.dispatch(
					UpdateFlexPeriodAction({
						update,
						original,
						update_visibility_students_groups: updateStudentsGroupsVisibility,
					})
				);
			});
	}

	DeleteFlexPeriod(id: number) {
		this.store
			.select(getFlexPeriodById(id))
			.pipe(take(1))
			.subscribe((toRemove) => {
				this.store.dispatch(DeleteFlexPeriodAction({ id, toRemove }));
			});
	}

	//#endregion

	//#region HTTP

	AddFlexPeriodHTTP(body: AddFlexScheduleRequest) {
		return this.http.post<FlexPeriod>('v2/flex_period/schedules/add', body, undefined, false);
	}

	EditFlexPeriodHTTP(body: UpdateFlexPeriodRequest) {
		return this.http.post<FlexPeriod>('v2/flex_period/update', body, undefined, false);
	}

	RemoveScheduleHTTP(id: number) {
		return this.http.post<FlexPeriod>('v2/flex_period/schedules/remove', { id: id }, undefined, false);
	}

	DeleteFlexPeriodHTTP(id: number) {
		return this.http.post<object>('v2/flex_period/remove', { id: id }, undefined, false);
	}

	GetAllFlexPeriodsHTTP() {
		return this.http.post<FlexPeriod[]>('v2/flex_period/list', {}, undefined, false);
	}

	GetFlexPeriodStatsHTTP(body: GetFlexPeriodStatsRequest): Observable<GetFlexPeriodStatsResponse> {
		return this.http.post<GetFlexPeriodStatsResponse>('v2/flex_period/stats', body, undefined, false);
	}

	//#endregion

	ScheduleItemString(schedule: FlexSchedule): string {
		const milTimePipe = new MilitaryTimeStringPipe();

		const days = schedule.days_of_week.map((day) => DaysOfWeek[day]).join(', ');
		const startTime = milTimePipe.transform(schedule.start_hour + ':' + schedule.start_minute);
		const endTime = milTimePipe.transform(schedule.end_hour + ':' + schedule.end_minute);

		return `Every ${days}, ${startTime} - ${endTime}`;
	}

	DisplayDaysString(days: number[]): string {
		if (days.length === 0) {
			return 'No days selected';
		}
		return 'Every ' + days.map((day) => DaysOfWeek[day]).join(', ');
	}

	openCreateFlex(): void {
		const dialogRef = this.matDialog.open(CreateFlexPeriodComponent, {
			panelClass: 'main-form-dialog-container',
			width: '500px',
		});
		dialogRef.componentInstance.closeDialog = () => {
			dialogRef.close();
		};
	}

	getDateStatus(date: Date): DateStatus {
		let status: DateStatus = 'today';
		if (moment(date).isBefore(moment(), 'day')) {
			status = 'past';
		}
		if (moment(date).isAfter(moment(), 'day')) {
			status = 'future';
		}
		return status;
	}
}
